import React, { createContext, ReactNode, useCallback, useContext, useMemo, useState } from "react";
import { DestinationCategory } from "../models/DestinationCategory";
import { TravelDuration } from "../models/TravelDuration";
import {discoveryTranslations} from "./discovery";
import {shortlistTranslations} from "./shortlists";

export type Language = "de" | "en";

type TranslationParams = Record<string, string | number>;

interface LanguageContextValue {
  language: Language;
  locale: string;
  t: (key: string, params?: TranslationParams) => string;
}

const translations: Record<Language, Record<string, string>> = {
  de: {
    ...discoveryTranslations.de,
    ...shortlistTranslations.de,
    "share.button": "Flug teilen",
    "share.loading": "Link wird erstellt …",
    "share.copied": "Link kopiert!",
    "share.copyManually": "Link auswählen und kopieren",
    "share.error": "Link konnte nicht erstellt werden. Bitte erneut versuchen.",
    "share.sharedTrip": "Mit dir geteilter Flug",
    "share.totalPrice": "Gesamtpreis pro Person",
    "share.tripLength": "Reiselänge",
    "share.days": "{count} Nächte",
    "share.route": "Verbindung",
    "share.leg": "Flug {count}",
    "share.flightLeg": "Teilstrecke",
    "share.priceOverview": "Preisübersicht",
    "share.separateBookings": "Diese Verbindung besteht aus einzelnen Buchungen.",
    "share.priceNotice": "Preisstand vom {date}. Flugpreise und Verfügbarkeit können sich ändern. Vor der Buchung beim Anbieter prüfen.",
    "share.unavailableTitle": "Dieser Fluglink ist nicht verfügbar",
    "share.unavailableText": "Der Link ist ungültig oder abgelaufen. Geteilte Fluglinks bleiben 90 Tage gültig.",
    "share.searchAgain": "Flüge entdecken",
    "share.loadingPage": "Geteilten Flug laden …",
    "visa.onlyEasy": "Auch eVisa, ETA & Visum bei Ankunft",
    "visa.shortEasy": "einfache Einreise",
    "visa.searchResults": "Passende Länder",
    "visa.loadingDestinations": "Passende Reiseziele werden geladen …",
    "visa.destinationsError": "Visa-Daten konnten nicht geladen werden. Erneut versuchen oder den Visa-Filter deaktivieren.",
    "visa.retry": "Erneut versuchen",
    "visa.destinationScope": "Die Zielauswahl berücksichtigt den Länderstatus. Aufenthaltsdauer und Transit werden mit der konkreten Flugsuche abgeglichen.",
    "visa.title": "Einreise & Visum berücksichtigen",
    "visa.status.entry_right_assumed": "Einreiserecht vorausgesetzt",
    "visa.departureAssumption": "Einreiserecht in {country} vorausgesetzt.",
    "visa.passport": "Reisepass-Land",
    "visa.off": "Nicht berücksichtigen",
    "visa.onlyFree": "Nur Ziele ohne zusätzliches Visum",
    "visa.shortFree": "ohne neues Visum",
    "visa.shortActive": "Visum-Info",
    "visa.remove": "Auswahl entfernen",
    "visa.scope": "Touristische Kurzreisen. Aktuelle Regeln vor Buchung prüfen.",
    "visa.filterScope": "Die beiden Filter sind Alternativen: nur grüne Ziele oder grüne und orange Ziele (eVisa, ETA, Visum bei Ankunft). Das Recht im Abflugland wird vorausgesetzt. Ungeprüfter Transit in anderen Ländern und unklare Aufenthaltsdauer werden ausgeschlossen.",
    "visa.indication": "Orientierung",
    "visa.datasetStatus": "Laut Datensatz",
    "visa.days": "Bis zu {days} Tage.",
    "visa.updated": "Stand: {date}",
    "visa.source": "Datenquelle",
    "visa.check": "Aktuelle Regeln prüfen",
    "visa.status.visa_free": "Visumfrei",
    "visa.status.visa_on_arrival": "Visum bei Ankunft",
    "visa.status.evisa": "eVisa erforderlich",
    "visa.status.eta": "Reisegenehmigung erforderlich",
    "visa.status.visa_required": "Visum vorab erforderlich",
    "visa.status.restricted": "Einreise eingeschränkt",
    "visa.status.unknown": "Keine Visa-Daten verfügbar",
    "visa.status.schengen": "Schengen-Visum erforderlich",
    "visa.status.countryVisa": "Visum für {country} erforderlich",
    "visa.reason.missing_data": "Keine ausreichenden Visa-Daten für diese Kombination verfügbar.",
    "visa.reason.transit": "Transit außerhalb des Abfluglandes ist für diese Verbindung nicht geprüft.",
    "visa.reason.duration_unknown": "Ohne Rückflug ist die Aufenthaltsdauer nicht bekannt.",
    "visa.reason.duration_exceeded": "Die Reisedauer liegt außerhalb des im Datensatz genannten visumfreien Aufenthalts.",
    "visa.resultsNote": "Reisepass-Orientierung · Datenstand 17.02.2026. Einreiserecht im Abflugland wird vorausgesetzt; Ausnahmen für andere Länder werden nicht abgeleitet.",
    "visa.empty": "Keine passenden Flüge. Visa-Filter oder Reiseziele anpassen.",
    "airport.clear": "Auswahl leeren",
    "airport.count": "{count}/{max} Abflughäfen",
    "airport.emptyAirports": "Keine passenden Abflugorte gefunden.",
    "airport.emptyCountries": "Keine passenden Länder gefunden.",
    "airport.error": "Abflugorte konnten nicht geladen werden.",
    "airport.loading": "Abflugorte werden geladen...",
    "airport.title": "Abflug von",

    "calendar.aria": "Datumsbereich auswählen",
    "calendar.nextMonth": "Nächster Monat",
    "calendar.previousMonth": "Voriger Monat",

    "category.activities": "Aktivitäten",
    "category.beach": "Strand",
    "category.camping": "Camping",
    "category.city": "Städtetrip",
    "category.culture": "Kultur",
    "category.mountains": "Berge",
    "category.party": "Party",
    "category.sun": "In die Sonne",
    "category.wellness": "Wellness",
    "category.winter": "Winterurlaub",

    "categorySelector.deselectAll": "Alles abwählen",
    "categorySelector.selectAll": "Alle auswählen",
    "categorySelector.title": "Wohin geht's?",

    "common.airports": "Orte",
    "common.done": "Auswahl übernehmen",
    "searchBar.search": "Flüge suchen",
    "calendar.fromMonth": "Ab Monat",
    "calendar.toMonth": "Bis Monat",
    "common.countries": "Länder",
    "common.email": "Deine E-Mail",
    "common.loading": "Wird überprüft...",
    "common.searchAirport": "Stadt oder Flughafen suchen",
    "common.searchCountry": "Land suchen",

    "comparison.title": "Preisvergleich",

    "connection.direct": "Direkt",
    "connection.directAndOneStop": "Direkt + 1 Stopp",
    "connection.directOnly": "Nur Direktflüge",
    "connection.oneStop": "1 Stopp",
    "connection.selfTransfer": "Selbst umsteigen",
    "connection.title": "Verbindung",
    "connection.via": "via {airport}",
    "connection.waitTime": "{duration} Aufenthalt",

    "destination.clear": "Auswahl leeren",
    "destination.close": "Schließen",
    "destination.emptyAirports": "Keine passenden Orte gefunden.",
    "destination.emptyCountries": "Keine passenden Länder gefunden.",
    "destination.error": "Ziele konnten nicht geladen werden.",
    "destination.oneStopCountriesDisabled": "Länder sind bei Umstieg deaktiviert.",
    "destination.oneStopLimit": "Bei Umstieg maximal {max} konkrete Ziele auswählen. Ausgewählt: {count}/{max}",
    "destination.requireDeparture": "Bitte zuerst einen Abflughafen wählen.",
    "destination.title": "Wohin soll es gehen?",

    "flight.available": "{count} Flüge verfügbar",
    "flight.book": "Zum Anbieter",
    "flight.bookLegs": "{count} Buchungen",
    "flight.bookLegsHint": "Öffnet {count} einzelne Ryanair-Buchungen in neuen Tabs.",
    "flight.estimatedPriceHint": "Preis basiert auf dem günstigsten Tagespreis.",
    "flight.fromPrice": "ab",
    "flight.inbound": "Rückflug",
    "flight.mixedCurrencyHint": "Gesamtpreis in Euro, Teilpreise umgerechnet.",
    "flight.oneWay": "Nur Hinflug",
    "flight.outbound": "Hinflug",
    "flight.period": "Reisezeitraum",
    "flight.timeSuffix": "Uhr",

    "groupTrip.description": "Finde den perfekten Trip\nfür die ganze Truppe.",
    "groupTrip.soon": "Soon",
    "groupTrip.title": "Gruppentrip",

    "home.aria": "Zur Startseite",
    "home.headlineLine1": "Finde die günstigsten Flüge",
    "home.headlineLine2": "egal wohin, egal wann.",

    "mobile.discover": "Jetzt entdecken",
    "mobile.steps": "Deine Flugsuche",
    "mobile.search": "Suchen",
    "mobile.resultsTitle": "Preis-\nvergleich",
    "mobile.editSearch": "Suche ändern",
    "mobile.details": "Flugdetails",
    "mobile.map": "{city} auf der Karte ansehen",
    "mobile.cards": "Bildkarten",
    "mobile.resultsView": "Ergebnisansicht",
    "mobile.emptyResults": "Keine Flüge gefunden. Passe deine Suche an.",
    "mobile.stepCount": "Schritt {step} von 3",
    "mobile.step1Title": "Von wo möchtest\ndu starten?",
    "mobile.step2Title": "Wann hast du Zeit?",
    "mobile.step3Title": "Was muss dein\nUrlaub bieten?",
    "mobile.next": "Weiter",
    "mobile.back": "Zurück",
    "mobile.refineDestination": "Ziele & Verbindungen eingrenzen",
    "mobile.duration": "Reisedauer",
    "mobile.moreTravelOptions": "Reiseart & eigene Dauer",
    "mobile.days": "Tage",
    "mobile.dateRange": "Zeitspanne, in der du reisen willst",
    "mobile.headlineLine1": "Günstige Flüge,",
    "mobile.headlineLine2": "egal wohin.",
    "mobileLock.headlineLine1": "Wir zeigen dir den",
    "mobileLock.headlineLine2": "günstigsten Flug in deinen",
    "mobileLock.headlineLine3": "nächsten Urlaub.",
    "mobileLock.noteLine1": "Bitte nutze den Browser deines",
    "mobileLock.noteLine2": "Computers, um farewise.de zu nutzen.",

    "page.categoryFallbackTitle": "Die günstigsten Flüge in deiner Wunschkategorie.",
    "page.categoryMonth": "Im {month}",
    "page.categoryTitle": "Die günstigsten Flüge zu deinem nächsten {category}urlaub.",
    "page.hotPicks": "Hot-Picks für Dich",
    "page.resultsTitle": "Die günstigsten Flüge für Deine Reiseziele",
    "page.searchTitle": "Ab in den Flieger.",

    "resultsCalendar.cheap": "günstig",
    "resultsCalendar.allDestinations": "Alle Ziele",
    "resultsCalendar.high": "teuer",
    "resultsCalendar.medium": "mittel",
    "resultsView.calendar": "Kalender",
    "resultsView.list": "Liste",

    "searchBar.categories": "Interessen",
    "searchBar.dateFrom": "Ab frühestens",
    "searchBar.dateTo": "Bis spätestens",
    "searchBar.departure": "Abflug",
    "searchBar.destination": "Wohin",
    "searchBar.everywhere": "Alle Ziele",
    "searchBar.placeholder": "-",
    "searchBar.compare": "Preise vergleichen",
    "searchBar.swapRoute": "Abflug und Ziel tauschen",

    "travel.calendar": "Kalender",
    "travel.customDuration": "Eigene Reisedauer in Tagen",
    "travel.duration.custom": "Eigene Dauer",
    "travel.duration.extended": "9-20 Tage",
    "travel.duration.long": "6-8 Tage",
    "travel.duration.medium": "3-5 Tage",
    "travel.duration.short": "2-3 Tage",
    "travel.max": "Max",
    "travel.min": "Min",
    "travel.oneWay": "Nur Hinflug",
    "travel.rangePlanner": "Zeitraumplaner",
    "travel.roundTrip": "Hin & Zurück",
    "travel.sampleMax": "z. B. 8",
    "travel.sampleMin": "z. B. 3",
    "travel.title": "Reisedauer & Zeitraum angeben",
    "travel.tripType": "Reiseart",

    "unlock.codePlaceholder": "Unlock-Code",
    "unlock.footer": "Du hast noch keinen Code? Kontaktiere",
    "unlock.submit": "Jetzt freischalten",
    "unlock.subtitle": "Exklusiver Zugang zu den besten Flugdeals. Gib deinen Code ein und leg los!",
    "unlock.title": "Günstige Flüge entdecken",
    "warning.missingSearchData": "Bitte gib ein Datum und einen Abflughafen an.",
  },
  en: {
    ...discoveryTranslations.en,
    ...shortlistTranslations.en,
    "share.button": "Share flight",
    "share.loading": "Creating link …",
    "share.copied": "Link copied!",
    "share.copyManually": "Select and copy this link",
    "share.error": "Could not create link. Please try again.",
    "share.sharedTrip": "A flight shared with you",
    "share.totalPrice": "Total per person",
    "share.tripLength": "Trip length",
    "share.days": "{count} nights",
    "share.route": "Route",
    "share.leg": "Flight {count}",
    "share.flightLeg": "Flight leg",
    "share.priceOverview": "Price overview",
    "share.separateBookings": "This itinerary requires separate bookings.",
    "share.priceNotice": "Price as of {date}. Fares and availability may change. Check with the provider before booking.",
    "share.unavailableTitle": "This flight link is unavailable",
    "share.unavailableText": "The link is invalid or expired. Shared flight links are valid for 90 days.",
    "share.searchAgain": "Explore flights",
    "share.loadingPage": "Loading shared flight …",
    "visa.onlyEasy": "Also eVisa, ETA & visa on arrival",
    "visa.shortEasy": "simple entry",
    "visa.searchResults": "Matching countries",
    "visa.loadingDestinations": "Loading matching destinations …",
    "visa.destinationsError": "Visa data could not be loaded. Retry or disable the visa filter.",
    "visa.retry": "Retry",
    "visa.destinationScope": "Destinations are filtered by country status. Stay length and transit are checked for the specific flight search.",
    "visa.title": "Consider entry & visa requirements",
    "visa.status.entry_right_assumed": "Entry rights assumed",
    "visa.departureAssumption": "Entry rights in {country} assumed.",
    "visa.passport": "Passport country",
    "visa.off": "Do not consider",
    "visa.onlyFree": "Only destinations without an additional visa",
    "visa.shortFree": "no new visa",
    "visa.shortActive": "visa info",
    "visa.remove": "Clear selection",
    "visa.scope": "Tourist trips. Check current rules before booking.",
    "visa.filterScope": "Choose either green destinations only or green and orange destinations (eVisa, ETA, visa on arrival). Rights in the departure country are assumed. Unchecked transit in other countries and unknown stay lengths are excluded.",
    "visa.indication": "guidance",
    "visa.datasetStatus": "According to dataset",
    "visa.days": "Up to {days} days.",
    "visa.updated": "As of: {date}",
    "visa.source": "Data source",
    "visa.check": "Check current rules",
    "visa.status.visa_free": "Visa-free",
    "visa.status.visa_on_arrival": "Visa on arrival",
    "visa.status.evisa": "eVisa required",
    "visa.status.eta": "Travel authorisation required",
    "visa.status.visa_required": "Visa required in advance",
    "visa.status.restricted": "Entry restricted",
    "visa.status.unknown": "Visa data unavailable",
    "visa.status.schengen": "Schengen visa required",
    "visa.status.countryVisa": "Visa required for {country}",
    "visa.reason.missing_data": "Insufficient visa data for this combination.",
    "visa.reason.transit": "Transit outside the departure country has not been checked for this itinerary.",
    "visa.reason.duration_unknown": "Stay duration is unknown without a return flight.",
    "visa.reason.duration_exceeded": "Trip duration is outside the visa-free stay listed in the dataset.",
    "visa.resultsNote": "Passport guidance · Data as of 17 Feb 2026. Entry rights in the departure country are assumed; exemptions for other countries are not inferred.",
    "visa.empty": "No matching flights. Adjust visa filters or destinations.",
    "airport.clear": "Clear selection",
    "airport.count": "{count}/{max} departure airports",
    "airport.emptyAirports": "No matching departure airports found.",
    "airport.emptyCountries": "No matching countries found.",
    "airport.error": "Departure airports could not be loaded.",
    "airport.loading": "Loading departure airports...",
    "airport.title": "Depart from",

    "calendar.aria": "Select date range",
    "calendar.nextMonth": "Next month",
    "calendar.previousMonth": "Previous month",

    "category.activities": "Activities",
    "category.beach": "Beach",
    "category.camping": "Camping",
    "category.city": "City trip",
    "category.culture": "Culture",
    "category.mountains": "Mountains",
    "category.party": "Party",
    "category.sun": "Into the sun",
    "category.wellness": "Wellness",
    "category.winter": "Winter trip",

    "categorySelector.deselectAll": "Deselect all",
    "categorySelector.selectAll": "Select all",
    "categorySelector.title": "What kind of trip?",

    "common.airports": "Places",
    "common.done": "Apply selection",
    "searchBar.search": "Search flights",
    "calendar.fromMonth": "From month",
    "calendar.toMonth": "Until month",
    "common.countries": "Countries",
    "common.email": "Your email",
    "common.loading": "Checking...",
    "common.searchAirport": "Search city or airport",
    "common.searchCountry": "Search country",

    "comparison.title": "Price comparison",

    "connection.direct": "Direct",
    "connection.directAndOneStop": "Direct + 1 stop",
    "connection.directOnly": "Direct flights only",
    "connection.oneStop": "1 stop",
    "connection.selfTransfer": "Self-transfer",
    "connection.title": "Connection",
    "connection.via": "via {airport}",
    "connection.waitTime": "{duration} layover",

    "destination.clear": "Clear selection",
    "destination.close": "Close",
    "destination.emptyAirports": "No matching places found.",
    "destination.emptyCountries": "No matching countries found.",
    "destination.error": "Destinations could not be loaded.",
    "destination.oneStopCountriesDisabled": "Countries are disabled for one-stop searches.",
    "destination.oneStopLimit": "Choose up to {max} specific destinations for one-stop searches. Selected: {count}/{max}",
    "destination.requireDeparture": "Please choose a departure airport first.",
    "destination.title": "Where do you want to go?",

    "flight.available": "{count} flights available",
    "flight.book": "View provider",
    "flight.bookLegs": "{count} bookings",
    "flight.bookLegsHint": "Opens {count} separate Ryanair bookings in new tabs.",
    "flight.estimatedPriceHint": "Price is based on the cheapest fare of that day.",
    "flight.fromPrice": "from",
    "flight.inbound": "Return",
    "flight.mixedCurrencyHint": "Total shown in euros, leg prices converted.",
    "flight.oneWay": "One way",
    "flight.outbound": "Outbound",
    "flight.period": "Travel dates",
    "flight.timeSuffix": "",

    "groupTrip.description": "Find the perfect trip\nfor the whole group.",
    "groupTrip.soon": "Soon",
    "groupTrip.title": "Group trip",

    "home.aria": "Go to homepage",
    "home.headlineLine1": "Find the cheapest flights",
    "home.headlineLine2": "anywhere, anytime.",

    "mobile.discover": "Start exploring",
    "mobile.steps": "Your flight search",
    "mobile.search": "Search",
    "mobile.resultsTitle": "Price\ncomparison",
    "mobile.editSearch": "Edit search",
    "mobile.details": "Flight details",
    "mobile.map": "View {city} on the map",
    "mobile.cards": "Photo cards",
    "mobile.resultsView": "Results view",
    "mobile.emptyResults": "No flights found. Try adjusting your search.",
    "mobile.stepCount": "Step {step} of 3",
    "mobile.step1Title": "Where are you starting from?",
    "mobile.step2Title": "When are you free?",
    "mobile.step3Title": "What are you in the mood for?",
    "mobile.next": "Next",
    "mobile.back": "Back",
    "mobile.refineDestination": "Refine destinations & connections",
    "mobile.duration": "Trip length",
    "mobile.moreTravelOptions": "Trip type & custom length",
    "mobile.days": "days",
    "mobile.dateRange": "When would you like to travel?",
    "mobile.headlineLine1": "Cheap flights,",
    "mobile.headlineLine2": "anywhere.",
    "mobileLock.headlineLine1": "We show you the",
    "mobileLock.headlineLine2": "cheapest flight for your",
    "mobileLock.headlineLine3": "next holiday.",
    "mobileLock.noteLine1": "Please use your computer's",
    "mobileLock.noteLine2": "browser to use farewise.de.",

    "page.categoryFallbackTitle": "The cheapest flights in your preferred category.",
    "page.categoryMonth": "In {month}",
    "page.categoryTitle": "The cheapest flights for your next {category} trip.",
    "page.hotPicks": "Hot picks for you",
    "page.resultsTitle": "The cheapest flights for your destinations",
    "page.searchTitle": "Ready for takeoff.",

    "resultsCalendar.cheap": "cheap",
    "resultsCalendar.allDestinations": "All destinations",
    "resultsCalendar.high": "high",
    "resultsCalendar.medium": "medium",
    "resultsView.calendar": "Calendar",
    "resultsView.list": "List",

    "searchBar.categories": "Interests",
    "searchBar.dateFrom": "From earliest",
    "searchBar.dateTo": "Until latest",
    "searchBar.departure": "Departure",
    "searchBar.destination": "Where to",
    "searchBar.everywhere": "All destinations",
    "searchBar.placeholder": "-",
    "searchBar.compare": "Compare prices",
    "searchBar.swapRoute": "Swap departure and destination",

    "travel.calendar": "Calendar",
    "travel.customDuration": "Custom trip length in days",
    "travel.duration.custom": "Custom length",
    "travel.duration.extended": "9-20 days",
    "travel.duration.long": "6-8 days",
    "travel.duration.medium": "3-5 days",
    "travel.duration.short": "2-3 days",
    "travel.max": "Max",
    "travel.min": "Min",
    "travel.oneWay": "One way",
    "travel.rangePlanner": "Range planner",
    "travel.roundTrip": "Round trip",
    "travel.sampleMax": "e.g. 8",
    "travel.sampleMin": "e.g. 3",
    "travel.title": "Set trip length & dates",
    "travel.tripType": "Trip type",

    "unlock.codePlaceholder": "Unlock code",
    "unlock.footer": "No code yet? Contact",
    "unlock.submit": "Unlock now",
    "unlock.subtitle": "Exclusive access to the best flight deals. Enter your code and get started!",
    "unlock.title": "Discover cheap flights",
    "warning.missingSearchData": "Please enter a date and a departure airport.",
  },
};

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

const detectLanguage = (): Language => {
  if (typeof navigator === "undefined") return "en";

  const languages = navigator.languages?.length ? navigator.languages : [navigator.language];
  return languages.some((language) => language.toLowerCase().startsWith("de")) ? "de" : "en";
};

const interpolate = (value: string, params?: TranslationParams) => {
  if (!params) return value;

  return Object.entries(params).reduce(
    (result, [key, replacement]) => result.replaceAll(`{${key}}`, String(replacement)),
    value
  );
};

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language] = useState<Language>(() => detectLanguage());
  const locale = language === "de" ? "de-DE" : "en-US";

  const t = useCallback(
    (key: string, params?: TranslationParams) =>
      interpolate(translations[language][key] ?? translations.en[key] ?? key, params),
    [language]
  );

  const value = useMemo(() => ({ language, locale, t }), [language, locale, t]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useTranslation = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useTranslation must be used inside LanguageProvider");
  }
  return context;
};

export const getCategoryLabel = (
  category: DestinationCategory,
  t: LanguageContextValue["t"]
) => t(`category.${category}`);

export const getTravelDurationLabel = (
  duration: TravelDuration,
  t: LanguageContextValue["t"]
) => {
  const keyByDuration: Record<TravelDuration, string> = {
    [TravelDuration.SHORT]: "travel.duration.short",
    [TravelDuration.MEDIUM]: "travel.duration.medium",
    [TravelDuration.LONG]: "travel.duration.long",
    [TravelDuration.EXTENDED]: "travel.duration.extended",
    [TravelDuration.CUSTOM]: "travel.duration.custom",
  };

  return t(keyByDuration[duration]);
};
